import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { 
  Lock, 
  Users, 
  DollarSign, 
  Shield, 
  TrendingUp, 
  Award,
  ArrowRight,
  CheckCircle,
  Target,
  FileText,
  Monitor,
  AlertTriangle,
  BarChart3,
  MessageCircle,
  XCircle,
  Database,
  Key,
  Link2,
  Layers,
  Server,
  Code
} from "lucide-react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { SEOHead } from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const metrics = [
  { label: "Active Operators", icon: Users, value: "—" },
  { label: "Revenue per License", icon: DollarSign, value: "—" },
  { label: "Enforcement Events", icon: Shield, value: "—" },
  { label: "Margin Stability", icon: TrendingUp, value: "—" },
  { label: "Certification Pass Rate", icon: Award, value: "—" },
];

const problemPoints = [
  "Founder-dependent businesses don't scale",
  "Consulting doesn't transfer",
  "Culture doesn't survive exits",
  "IP leaks through people",
];

const solutionPoints = [
  "Enforceable operating authority",
  "Licensed execution standards",
  "Operator substitution",
  "Governance continuity",
];

const modelComponents = [
  { term: "Certification", result: "Access" },
  { term: "Licensing", result: "Revenue" },
  { term: "Enforcement", result: "Protection" },
  { term: "Governance", result: "Durability" },
];

const whyItWins = [
  "Founder-independent",
  "License-revocable",
  "Operator-replaceable",
  "Audit-ready",
  "Exit-architected",
];

const InvestorAccessPage = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate("/admin");
    }
  }, [isAuthenticated, isLoading, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#080808] flex items-center justify-center">
        <div className="text-center">
          <Lock className="h-12 w-12 text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Verifying access...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#080808] flex items-center justify-center">
        <div className="text-center">
          <Lock className="h-12 w-12 text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080808]" data-testid="investor-access-page">
      <SEOHead 
        title="Investor Access - Confidential"
        description="Confidential investor metrics and overview for OnPoint Authority Systems."
        url="https://onpointauthority.com/investor-access"
      />
      <Navbar />

      {/* Confidential Banner */}
      <div className="bg-primary/10 border-b border-primary/20 py-3 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-3">
          <Lock className="h-4 w-4 text-primary" />
          <p className="text-xs text-primary font-medium tracking-wider uppercase">
            Investor View — Metrics Only — No Learning Access
          </p>
        </div>
      </div>

      {/* Metrics Dashboard */}
      <section className="pt-24 pb-16 md:pt-32 md:pb-24 px-6 md:px-12" data-testid="metrics-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              Performance Metrics
            </p>
            <h1 className="font-serif text-5xl md:text-6xl font-light tracking-tight leading-[0.95] text-foreground">
              Investor<br />
              <span className="text-primary">Dashboard</span>
            </h1>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {metrics.map((metric, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-card/5 border border-white/10 p-6 text-center"
                data-testid={`metric-${metric.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <metric.icon className="h-6 w-6 text-primary mx-auto mb-3" strokeWidth={1.5} />
                <p className="text-2xl font-mono text-foreground mb-2">{metric.value}</p>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">{metric.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Opening Statement */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#050505] border-t border-white/5" data-testid="opening-section">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-8">
              Investor Pitch Narrative
            </p>
            <blockquote className="font-serif text-3xl md:text-4xl font-light text-foreground leading-relaxed mb-6">
              "This is not a content company.<br />
              <span className="text-primary">It is an authority licensing business disguised as education."</span>
            </blockquote>
          </motion.div>
        </div>
      </section>

      {/* The Problem */}
      <section className="py-16 md:py-24 px-6 md:px-12 border-t border-white/5" data-testid="problem-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <p className="text-xs font-medium tracking-[0.2em] uppercase text-red-400 mb-4">
                The Problem
              </p>
              <ul className="space-y-4 mb-8">
                {problemPoints.map((point, index) => (
                  <li key={index} className="flex items-start gap-3 text-lg text-muted-foreground">
                    <div className="w-1.5 h-1.5 bg-red-400 rounded-full mt-3" />
                    {point}
                  </li>
                ))}
              </ul>
              <p className="text-xl font-serif text-foreground border-l-2 border-primary pl-6">
                Buyers don't buy knowledge.<br />
                <span className="text-primary">They buy control systems.</span>
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <p className="text-xs font-medium tracking-[0.2em] uppercase text-primary mb-4">
                The Solution
              </p>
              <p className="text-muted-foreground mb-6">
                OnPoint Authority Systems engineers:
              </p>
              <ul className="space-y-4 mb-8">
                {solutionPoints.map((point, index) => (
                  <li key={index} className="flex items-start gap-3 text-lg text-foreground">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-1" strokeWidth={1.5} />
                    {point}
                  </li>
                ))}
              </ul>
              <p className="text-xl font-serif text-primary">
                This business runs without heroics.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* The Model */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#050505]" data-testid="model-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              The Model
            </p>
            <h2 className="font-serif text-4xl font-light text-foreground">
              Revenue <span className="text-primary">Architecture</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {modelComponents.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-card/5 border border-white/10 p-6 text-center"
              >
                <p className="text-sm text-muted-foreground uppercase tracking-wider mb-2">{item.term}</p>
                <p className="text-xs text-primary mb-3">=</p>
                <p className="text-xl font-serif text-foreground">{item.result}</p>
              </motion.div>
            ))}
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="text-center text-xl font-serif text-primary mt-10"
          >
            Every dollar earned is defended.
          </motion.p>
        </div>
      </section>

      {/* Why It Wins */}
      <section className="py-16 md:py-24 px-6 md:px-12 border-t border-white/5" data-testid="why-wins-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              Competitive Advantage
            </p>
            <h2 className="font-serif text-4xl font-light text-foreground">
              Why It <span className="text-primary">Wins</span>
            </h2>
          </motion.div>

          <div className="flex flex-wrap gap-4 mb-12">
            {whyItWins.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="bg-primary/10 border border-primary/30 px-6 py-3"
              >
                <span className="text-sm text-primary font-medium">{item}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Licensing Enforcement SOPs */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#050505]" data-testid="sops-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-red-400 mb-4">
              Valuation Protection
            </p>
            <h2 className="font-serif text-4xl font-light text-foreground">
              Licensing Enforcement <span className="text-primary">SOPs</span>
            </h2>
          </motion.div>

          {/* SOP 1: Authority Violation Detection */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-8 bg-card/5 border border-white/10 p-6"
          >
            <h3 className="text-sm font-mono text-primary mb-4">SOP 1: Authority Violation Detection</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3">Triggers</p>
                <ul className="space-y-2">
                  {["Pricing deviation", "Process bypass", "Unauthorized representation", "Brand misuse", "Operator scope creep"].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-foreground">
                      <div className="w-1 h-1 bg-red-400 rounded-full" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3">Detection Sources</p>
                <ul className="space-y-2">
                  {["Portal analytics", "Audit reviews", "Client complaints", "Internal reporting"].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-foreground">
                      <div className="w-1 h-1 bg-primary rounded-full" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>

          {/* SOP 2: Violation Classification */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-8"
          >
            <h3 className="text-sm font-mono text-primary mb-4">SOP 2: Violation Classification</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Level 1 */}
              <div className="bg-yellow-500/5 border border-yellow-500/20 p-5">
                <p className="text-xs font-mono text-yellow-400 mb-2">Level 1 — Minor Drift</p>
                <ul className="space-y-1 mb-4">
                  <li className="text-sm text-muted-foreground">• Documentation error</li>
                  <li className="text-sm text-muted-foreground">• Non-material delay</li>
                </ul>
                <p className="text-xs text-muted-foreground uppercase mb-2">Action:</p>
                <ul className="space-y-1">
                  <li className="text-sm text-foreground">• Notice issued</li>
                  <li className="text-sm text-foreground">• Correction required</li>
                  <li className="text-sm text-foreground">• Logged</li>
                </ul>
              </div>
              {/* Level 2 */}
              <div className="bg-orange-500/5 border border-orange-500/20 p-5">
                <p className="text-xs font-mono text-orange-400 mb-2">Level 2 — Authority Breach</p>
                <ul className="space-y-1 mb-4">
                  <li className="text-sm text-muted-foreground">• Decision override</li>
                  <li className="text-sm text-muted-foreground">• Margin erosion</li>
                  <li className="text-sm text-muted-foreground">• System modification</li>
                </ul>
                <p className="text-xs text-muted-foreground uppercase mb-2">Action:</p>
                <ul className="space-y-1">
                  <li className="text-sm text-foreground">• Immediate conditional status</li>
                  <li className="text-sm text-foreground">• Access restricted</li>
                  <li className="text-sm text-foreground">• Mandatory review</li>
                </ul>
              </div>
              {/* Level 3 */}
              <div className="bg-red-500/5 border border-red-500/20 p-5">
                <p className="text-xs font-mono text-red-400 mb-2">Level 3 — License Breach</p>
                <ul className="space-y-1 mb-4">
                  <li className="text-sm text-muted-foreground">• Misrepresentation</li>
                  <li className="text-sm text-muted-foreground">• IP misuse</li>
                  <li className="text-sm text-muted-foreground">• Unauthorized sub-licensing</li>
                </ul>
                <p className="text-xs text-muted-foreground uppercase mb-2">Action:</p>
                <ul className="space-y-1">
                  <li className="text-sm text-foreground">• Immediate revocation</li>
                  <li className="text-sm text-foreground">• Public decertification</li>
                  <li className="text-sm text-foreground">• Legal escalation</li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* SOP 3 & 4 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-card/5 border border-white/10 p-5"
            >
              <h3 className="text-sm font-mono text-primary mb-4">SOP 3: Enforcement Execution</h3>
              <ul className="space-y-2 mb-4">
                {["No negotiation", "No retroactive approval", "No emotional handling", "All actions logged"].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-foreground">
                    <Shield className="h-3 w-3 text-primary" />
                    {item}
                  </li>
                ))}
              </ul>
              <p className="text-sm font-serif text-primary italic">
                Authority is enforced once, not debated forever.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-card/5 border border-white/10 p-5"
            >
              <h3 className="text-sm font-mono text-primary mb-4">SOP 4: Reinstatement (If Allowed)</h3>
              <p className="text-xs text-muted-foreground uppercase mb-3">Requirements:</p>
              <ul className="space-y-2">
                {["Full remediation", "Re-certification", "Board approval (for Level 3)", "Higher audit frequency"].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-foreground">
                    <CheckCircle className="h-3 w-3 text-primary" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Acquisition-Ready Data Room */}
      <section className="py-16 md:py-24 px-6 md:px-12 border-t border-white/5" data-testid="data-room-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              Mapped to Buyer Diligence
            </p>
            <h2 className="font-serif text-4xl font-light text-foreground">
              Acquisition-Ready <span className="text-primary">Data Room</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { id: "00", name: "READ_ME_FIRST", items: ["CIM", "Executive Summary", "Data Room Index", "Process Timeline"] },
              { id: "01", name: "CORPORATE", items: ["Certificate of Incorporation", "Cap Table", "Board Consents", "IP Assignments", "Governance Charter"] },
              { id: "02", name: "INTELLECTUAL_PROPERTY", items: ["IP Assignment Agreements", "Trademarks", "Copyrights", "Licensing Agreements", "Enforcement SOPs"] },
              { id: "03", name: "PRODUCT & SYSTEMS", items: ["Authority System Architecture", "Certification Curriculum", "Operator Playbooks", "Governance Models"] },
              { id: "04", name: "REVENUE & ECONOMICS", items: ["Pricing Tiers", "SKU Matrix", "ARR Forecast", "Valuation Sensitivity", "Unit Economics"] },
              { id: "05", name: "OPERATIONS", items: ["Operator Substitution Plans", "Portal UX Logic", "Audit Processes", "Compliance Metrics"] },
              { id: "06", name: "CUSTOMERS & LICENSEES", items: ["Licensee List", "Contract Summaries", "Retention Metrics", "Enforcement History (Redacted)"] },
              { id: "07", name: "RISK & COMPLIANCE", items: ["Risk Register", "Mitigation Plans", "Legal Reviews", "Insurance"] },
              { id: "08", name: "EXIT READINESS", items: ["Founder Transition Plan", "Management Presentation", "LOI Scenarios", "Exclusivity Governance"] },
              { id: "09", name: "INVESTOR MATERIALS", items: ["Pitch Narrative", "Board Packs", "IC Memos", "Teasers & NDA"] },
            ].map((folder, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="bg-card/5 border border-white/10 p-5 hover:border-primary/30 transition-colors"
              >
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-xs font-mono text-primary">{folder.id}</span>
                  <span className="text-sm font-medium text-foreground">{folder.name}</span>
                </div>
                <ul className="space-y-1.5">
                  {folder.items.map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
                      <FileText className="h-3 w-3 text-muted-foreground/50" />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Live Portal Wireframes */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#050505]" data-testid="wireframes-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              Authority-Grade UX
            </p>
            <h2 className="font-serif text-4xl font-light text-foreground">
              Live Portal <span className="text-primary">Wireframes</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Login Screen */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-card/5 border border-white/10 p-6"
            >
              <div className="flex items-center gap-2 mb-4">
                <Lock className="h-4 w-4 text-primary" />
                <h3 className="text-sm font-mono text-primary">LOGIN SCREEN (AUTHORITY GATE)</h3>
              </div>
              <p className="text-xs text-muted-foreground mb-4">Purpose: Signal enforcement immediately.</p>
              <div className="space-y-2 mb-4">
                <p className="text-xs text-muted-foreground uppercase">Elements:</p>
                {["Logo (small, top center)", "Title: OnPoint Authority Systems Portal", "Subtext: Licensed Access Only", "Email + MFA", "License Status Check (server-side)"].map((item, i) => (
                  <p key={i} className="text-sm text-foreground pl-3 border-l border-white/10">{item}</p>
                ))}
              </div>
              <div className="bg-red-500/10 border border-red-500/20 p-3 mt-4">
                <p className="text-xs text-red-400">Behavior: License expired → hard stop + compliance notice</p>
              </div>
            </motion.div>

            {/* Operator Dashboard */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-card/5 border border-white/10 p-6"
            >
              <div className="flex items-center gap-2 mb-4">
                <Monitor className="h-4 w-4 text-primary" />
                <h3 className="text-sm font-mono text-primary">OPERATOR DASHBOARD</h3>
              </div>
              <div className="flex gap-4 mb-4 text-xs">
                <span className="text-green-400">🟢 Active</span>
                <span className="text-yellow-400">🟡 Conditional</span>
                <span className="text-red-400">🔴 Revoked</span>
              </div>
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-muted-foreground uppercase mb-1">Section 1: Authority Mandates</p>
                  <p className="text-sm text-foreground">Current operating mandates, Non-negotiable standards</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase mb-1">Section 2: Required Actions</p>
                  <p className="text-sm text-foreground">Open exams, Compliance tasks, Remediation steps</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase mb-1">Section 3: Certification Progress</p>
                  <p className="text-sm text-foreground">Module completion, Exam pass/fail</p>
                </div>
              </div>
            </motion.div>

            {/* Certification Module */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-card/5 border border-white/10 p-6"
            >
              <div className="flex items-center gap-2 mb-4">
                <Award className="h-4 w-4 text-primary" />
                <h3 className="text-sm font-mono text-primary">CERTIFICATION MODULE VIEW</h3>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground uppercase mb-2">Left Sidebar:</p>
                  <ul className="space-y-1 text-sm text-foreground">
                    <li>• Locked progression tree</li>
                    <li>• Completed / Locked / Pending</li>
                  </ul>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase mb-2">Main Panel:</p>
                  <ul className="space-y-1 text-sm text-foreground">
                    <li>• Written frameworks</li>
                    <li>• Case studies</li>
                    <li>• "Authority Rationale" sections</li>
                  </ul>
                </div>
              </div>
              <div className="bg-yellow-500/10 border border-yellow-500/20 p-3 mt-4">
                <p className="text-xs text-yellow-400">Warning: Certification does not guarantee license continuity</p>
              </div>
            </motion.div>

            {/* Enforcement View */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-card/5 border border-white/10 p-6"
            >
              <div className="flex items-center gap-2 mb-4">
                <AlertTriangle className="h-4 w-4 text-red-400" />
                <h3 className="text-sm font-mono text-red-400">ENFORCEMENT VIEW (INTERNAL ONLY)</h3>
              </div>
              <p className="text-xs text-muted-foreground uppercase mb-3">Violation Dashboard Columns:</p>
              <div className="flex flex-wrap gap-2 mb-4">
                {["Operator", "Severity Level", "Trigger Source", "Status"].map((col, i) => (
                  <span key={i} className="text-xs bg-white/5 px-2 py-1 text-foreground">{col}</span>
                ))}
              </div>
              <p className="text-xs text-muted-foreground uppercase mb-2">Actions:</p>
              <div className="flex flex-wrap gap-2">
                {["Escalate", "Restrict Access", "Revoke License", "Log Resolution"].map((action, i) => (
                  <span key={i} className="text-xs bg-red-500/10 border border-red-500/20 px-2 py-1 text-red-400">{action}</span>
                ))}
              </div>
            </motion.div>

            {/* Investor/Board View */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="bg-card/5 border border-primary/30 p-6 lg:col-span-2"
            >
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 className="h-4 w-4 text-primary" />
                <h3 className="text-sm font-mono text-primary">INVESTOR / BOARD VIEW (READ-ONLY)</h3>
              </div>
              <p className="text-xs text-muted-foreground mb-4">Metrics Only — No content access. No narratives. Just proof.</p>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {["Active Licenses", "Revenue / License", "Certification Pass Rate", "Enforcement Events (90d)", "License Churn"].map((metric, i) => (
                  <div key={i} className="bg-white/5 p-3 text-center">
                    <p className="text-xs text-muted-foreground">{metric}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Investor Q&A Rehearsals */}
      <section className="py-16 md:py-24 px-6 md:px-12 border-t border-white/5" data-testid="qa-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              What They Ask Behind Closed Doors
            </p>
            <h2 className="font-serif text-4xl font-light text-foreground">
              Investor Q&A <span className="text-primary">Rehearsals</span>
            </h2>
          </motion.div>

          <div className="space-y-4">
            {[
              { q: "Is this just a consulting business with branding?", a: "No. Consulting doesn't revoke access. We can shut off an operator in minutes." },
              { q: "What happens if the founder leaves?", a: "Nothing operationally. Authority is system-embedded, not personality-dependent." },
              { q: "How defensible is this IP?", a: "Access-controlled, licensed, audited, and revocable. Leakage is structurally prevented." },
              { q: "What prevents operators from copying this?", a: "They don't have system authority, enforcement rights, or governance control. Knowledge without authority is useless." },
              { q: "Where is the risk?", a: "Operator quality drift. Which is why enforcement is automated, not cultural." },
              { q: "Why hasn't someone else done this?", a: "Because it requires saying no, revoking access, and enforcing standards. Most businesses are unwilling to do that." },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="bg-card/5 border border-white/10 p-6"
              >
                <p className="text-sm text-primary mb-3 flex items-start gap-2">
                  <MessageCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  "{item.q}"
                </p>
                <p className="text-foreground pl-6 border-l-2 border-primary">{item.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* License Pricing Optimization */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#050505]" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              Buyers Care About This More Than Growth
            </p>
            <h2 className="font-serif text-4xl font-light text-foreground">
              License Pricing <span className="text-primary">Optimization</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {/* Tier 1 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-card/5 border border-white/10 p-6"
            >
              <div className="flex items-center gap-2 mb-4">
                <span className="text-2xl">🥉</span>
                <div>
                  <h3 className="text-sm font-medium text-foreground">Tier 1</h3>
                  <p className="text-xs text-muted-foreground">Certified Operator</p>
                </div>
              </div>
              <p className="text-3xl font-mono text-primary mb-4">$15,000<span className="text-sm text-muted-foreground">/year</span></p>
              <div className="space-y-2 text-sm">
                <p className="text-foreground">Margin: ~85%</p>
                <p className="text-muted-foreground">Access: Core frameworks</p>
                <p className="text-muted-foreground">Enforcement: Standard</p>
              </div>
            </motion.div>

            {/* Tier 2 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-card/5 border border-primary/30 p-6"
            >
              <div className="flex items-center gap-2 mb-4">
                <span className="text-2xl">🥈</span>
                <div>
                  <h3 className="text-sm font-medium text-foreground">Tier 2</h3>
                  <p className="text-xs text-muted-foreground">Licensed Authority Operator</p>
                </div>
              </div>
              <p className="text-3xl font-mono text-primary mb-4">$35,000<span className="text-sm text-muted-foreground">/year</span></p>
              <div className="space-y-2 text-sm">
                <p className="text-foreground">Margin: ~88%</p>
                <p className="text-muted-foreground">Access: Advanced systems + portal</p>
                <p className="text-muted-foreground">Enforcement: Quarterly audits</p>
              </div>
            </motion.div>

            {/* Tier 3 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-primary/10 border border-primary/50 p-6"
            >
              <div className="flex items-center gap-2 mb-4">
                <span className="text-2xl">🥇</span>
                <div>
                  <h3 className="text-sm font-medium text-foreground">Tier 3</h3>
                  <p className="text-xs text-muted-foreground">Enterprise Authority License</p>
                </div>
              </div>
              <p className="text-3xl font-mono text-primary mb-4">$75K–$100K<span className="text-sm text-muted-foreground">/year</span></p>
              <div className="space-y-2 text-sm">
                <p className="text-foreground">Margin: ~90%</p>
                <p className="text-muted-foreground">Access: Full system + governance</p>
                <p className="text-muted-foreground">Enforcement: Continuous monitoring</p>
              </div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-card/5 border border-white/10 p-6"
          >
            <h3 className="text-sm font-mono text-primary mb-4">PRICING LEVERS BUYERS LOVE</h3>
            <div className="flex flex-wrap gap-3">
              {["Raise price without new cost", "Reduce licenses to improve quality", "Add audit fees", "Add re-certification fees", "Add geographic exclusivity premiums"].map((lever, i) => (
                <span key={i} className="text-sm bg-primary/10 text-primary px-3 py-1.5">{lever}</span>
              ))}
            </div>
            <p className="text-sm font-serif text-primary mt-6 italic">Pricing power is embedded, not negotiated.</p>
          </motion.div>
        </div>
      </section>

      {/* Banker Selection + Mandate */}
      <section className="py-16 md:py-24 px-6 md:px-12 border-t border-white/5" data-testid="banker-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              Transaction Readiness
            </p>
            <h2 className="font-serif text-4xl font-light text-foreground">
              Banker Selection <span className="text-primary">& Mandate</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Banker Scorecard */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-card/5 border border-white/10 p-6"
            >
              <h3 className="text-sm font-mono text-primary mb-6">BANKER SCORECARD</h3>
              <div className="space-y-3">
                {[
                  { criteria: "IP Transaction Experience", weight: "25%" },
                  { criteria: "Licensing / SaaS Hybrid Deals", weight: "20%" },
                  { criteria: "Founder Transition Experience", weight: "15%" },
                  { criteria: "Mid-Market PE Access", weight: "20%" },
                  { criteria: "Discipline / Process", weight: "20%" },
                ].map((item, i) => (
                  <div key={i} className="flex justify-between items-center border-b border-white/5 pb-2">
                    <span className="text-sm text-foreground">{item.criteria}</span>
                    <span className="text-sm font-mono text-primary">{item.weight}</span>
                  </div>
                ))}
              </div>
              <div className="bg-red-500/10 border border-red-500/20 p-3 mt-6">
                <p className="text-xs text-red-400 flex items-center gap-2">
                  <XCircle className="h-4 w-4" />
                  Red flag: "We'll figure out positioning together."
                </p>
              </div>
            </motion.div>

            {/* Mandate Draft */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-card/5 border border-white/10 p-6"
            >
              <h3 className="text-sm font-mono text-primary mb-6">BANKER MANDATE (DRAFT)</h3>
              
              <div className="space-y-4">
                <div>
                  <p className="text-xs text-muted-foreground uppercase mb-2">Scope:</p>
                  <ul className="space-y-1 text-sm text-foreground">
                    <li>• Market OnPoint Authority Systems, Inc.</li>
                    <li>• Position as authority licensing platform</li>
                    <li>• Run controlled sell-side process</li>
                    <li>• Manage NDAs, IOIs, LOIs</li>
                    <li>• Support diligence & SPA negotiation</li>
                  </ul>
                </div>
                
                <div>
                  <p className="text-xs text-muted-foreground uppercase mb-2">Exclusions:</p>
                  <ul className="space-y-1 text-sm text-foreground">
                    <li>• No operational control</li>
                    <li>• No pricing authority</li>
                    <li>• No license restructuring without approval</li>
                  </ul>
                </div>
                
                <div>
                  <p className="text-xs text-muted-foreground uppercase mb-2">Fees:</p>
                  <ul className="space-y-1 text-sm text-foreground">
                    <li>• Success-based</li>
                    <li>• No upfront retainers preferred</li>
                    <li>• Clear tail provision</li>
                  </ul>
                </div>
              </div>
              
              <div className="bg-primary/10 border border-primary/30 p-4 mt-6">
                <p className="text-xs text-muted-foreground uppercase mb-2">Positioning Language:</p>
                <p className="text-sm font-serif text-primary italic">
                  "This is not an education business. It is an authority licensing platform with enforcement infrastructure."
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Technical Architecture - CMS Collections */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#050505]" data-testid="cms-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              Webflow Architecture + Permission Logic
            </p>
            <h2 className="font-serif text-4xl font-light text-foreground">
              CMS <span className="text-primary">Collections</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              {
                name: "Authority_Content",
                purpose: "Public legitimacy + silent selling",
                fields: ["Title", "Slug", "Section", "Visibility", "Content Body", "Diagram Image", "Authority Principle Tag"]
              },
              {
                name: "Certification_Modules",
                purpose: "Core educational + enforcement asset",
                fields: ["Module Number", "Module Title", "Tier Required", "Authority Objective", "Doctrine", "Operating Procedure", "Enforcement Rule", "Exam ID", "Pass Requirement", "Locked Until Previous Complete"]
              },
              {
                name: "Exams",
                purpose: "Certification gating",
                fields: ["Exam ID", "Module Linked", "Question Bank", "Pass Score", "Time Limit", "Retake Policy", "Auto-Fail Conditions"]
              },
              {
                name: "Operators",
                purpose: "Single source of truth for license status",
                fields: ["Operator Name", "User Email", "License Tier", "License Status", "Certification Progress %", "Last Audit Date", "Next Audit Due", "Enforcement Flags", "Revenue Reporting"]
              },
              {
                name: "Investors_Board",
                purpose: "Read-only authority",
                fields: ["Name", "Role", "Access Level", "NDA Signed", "Metrics Visibility"]
              },
              {
                name: "Enforcement_Log",
                purpose: "This is the moat",
                fields: ["Operator Linked", "Violation Type", "Severity", "Date Issued", "Required Remediation", "Status", "Revocation Trigger"]
              },
            ].map((collection, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className={`bg-card/5 border p-5 ${collection.name === "Enforcement_Log" ? "border-red-500/30" : "border-white/10"}`}
              >
                <div className="flex items-center gap-2 mb-3">
                  <Database className={`h-4 w-4 ${collection.name === "Enforcement_Log" ? "text-red-400" : "text-primary"}`} />
                  <h3 className={`text-sm font-mono ${collection.name === "Enforcement_Log" ? "text-red-400" : "text-primary"}`}>{collection.name}</h3>
                </div>
                <p className="text-xs text-muted-foreground mb-3">{collection.purpose}</p>
                <div className="flex flex-wrap gap-1">
                  {collection.fields.slice(0, 5).map((field, i) => (
                    <span key={i} className="text-xs bg-white/5 px-2 py-0.5 text-foreground">{field}</span>
                  ))}
                  {collection.fields.length > 5 && (
                    <span className="text-xs text-muted-foreground">+{collection.fields.length - 5} more</span>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* User Roles */}
      <section className="py-16 md:py-24 px-6 md:px-12 border-t border-white/5" data-testid="roles-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
                Webflow Membership
              </p>
              <h2 className="font-serif text-4xl font-light text-foreground mb-8">
                User <span className="text-primary">Roles</span>
              </h2>
              
              <div className="space-y-3">
                {[
                  { role: "Public", purpose: "Read-only authority" },
                  { role: "Applicant", purpose: "Assessment + application" },
                  { role: "Certified Operator", purpose: "Access to certification" },
                  { role: "Licensed Operator", purpose: "Active license + dashboard" },
                  { role: "Investor", purpose: "Metrics only" },
                  { role: "Board", purpose: "Metrics + governance" },
                  { role: "Admin", purpose: "Full control" },
                ].map((item, i) => (
                  <div key={i} className="flex justify-between items-center p-3 bg-card/5 border border-white/10">
                    <span className="text-sm font-medium text-foreground">{item.role}</span>
                    <span className="text-sm text-muted-foreground">{item.purpose}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
                Clean + Intentional
              </p>
              <h2 className="font-serif text-4xl font-light text-foreground mb-8">
                URL <span className="text-primary">Structure</span>
              </h2>

              <div className="space-y-4">
                <div className="bg-card/5 border border-white/10 p-4">
                  <p className="text-xs text-muted-foreground uppercase mb-2">🌐 Public Authority Layer</p>
                  <div className="font-mono text-xs text-foreground space-y-1">
                    <p>/systems, /manifesto, /governance</p>
                    <p>/certification, /investor-access, /operator-application</p>
                  </div>
                </div>
                <div className="bg-card/5 border border-yellow-500/20 p-4">
                  <p className="text-xs text-yellow-400 uppercase mb-2">🔐 Applicant Layer</p>
                  <div className="font-mono text-xs text-foreground space-y-1">
                    <p>/apply, /assessment, /application-status</p>
                  </div>
                </div>
                <div className="bg-card/5 border border-primary/30 p-4">
                  <p className="text-xs text-primary uppercase mb-2">🧠 Certification Portal</p>
                  <div className="font-mono text-xs text-foreground space-y-1">
                    <p>/portal, /portal/dashboard, /portal/modules</p>
                    <p>/portal/module/&#123;slug&#125;, /portal/exams, /portal/enforcement</p>
                  </div>
                </div>
                <div className="bg-card/5 border border-blue-500/20 p-4">
                  <p className="text-xs text-blue-400 uppercase mb-2">🏛 Investor / Board View</p>
                  <div className="font-mono text-xs text-foreground space-y-1">
                    <p>/investor, /investor/metrics, /investor/governance</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Permission Logic */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#050505]" data-testid="permission-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              By License Tier
            </p>
            <h2 className="font-serif text-4xl font-light text-foreground">
              Permission <span className="text-primary">Logic</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* License Tiers */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-card/5 border border-white/10 p-6"
            >
              <div className="flex items-center gap-2 mb-6">
                <Key className="h-4 w-4 text-primary" />
                <h3 className="text-sm font-mono text-primary">LICENSE TIERS</h3>
              </div>
              <div className="space-y-3">
                {[
                  { tier: "Foundation", access: "Modules 1–4" },
                  { tier: "Operator", access: "Modules 1–8" },
                  { tier: "Master", access: "All modules + audit rights" },
                ].map((item, i) => (
                  <div key={i} className="flex justify-between items-center p-3 border border-white/5">
                    <span className="text-sm font-medium text-foreground">{item.tier}</span>
                    <span className="text-sm text-muted-foreground">{item.access}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Conditional Logic */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-card/5 border border-white/10 p-6"
            >
              <div className="flex items-center gap-2 mb-6">
                <Code className="h-4 w-4 text-primary" />
                <h3 className="text-sm font-mono text-primary">CONDITIONAL VISIBILITY</h3>
              </div>
              <div className="font-mono text-xs space-y-2 mb-4">
                <p className="text-blue-400">IF</p>
                <p className="text-foreground pl-4">User Role = Operator</p>
                <p className="text-foreground pl-4">AND License Status = Active</p>
                <p className="text-foreground pl-4">AND Certification Progress ≥ Required</p>
                <p className="text-green-400">THEN</p>
                <p className="text-foreground pl-4">Show module, Enable exam, Show metrics</p>
                <p className="text-red-400">ELSE</p>
                <p className="text-foreground pl-4">Show compliance notice, Lock content</p>
              </div>
              <div className="bg-red-500/10 border border-red-500/20 p-3 mt-4">
                <p className="text-xs text-red-400 font-mono">
                  IF License Status ≠ Active → All content locked → Dashboard shows violation notice only
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="py-16 md:py-24 px-6 md:px-12 border-t border-white/5" data-testid="tech-stack-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              Minimal, Clean
            </p>
            <h2 className="font-serif text-4xl font-light text-foreground">
              Tech <span className="text-primary">Stack</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            {[
              { name: "Webflow", desc: "Frontend + CMS" },
              { name: "Memberstack", desc: "Auth" },
              { name: "Wized", desc: "Conditional logic" },
              { name: "Stripe", desc: "Licensing payments" },
              { name: "Airtable", desc: "Audits + logs" },
              { name: "Custom", desc: "Exam tool" },
            ].map((tech, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="bg-card/5 border border-white/10 p-4 text-center"
              >
                <Server className="h-5 w-5 text-primary mx-auto mb-2" />
                <p className="text-sm font-medium text-foreground">{tech.name}</p>
                <p className="text-xs text-muted-foreground">{tech.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-4"
          >
            {["No LMS.", "No funnel software.", "No SaaS sprawl."].map((item, i) => (
              <div key={i} className="bg-red-500/10 border border-red-500/20 p-4 text-center">
                <XCircle className="h-5 w-5 text-red-400 mx-auto mb-2" />
                <p className="text-sm text-red-400">{item}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Why Buyers Love This */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#050505]" data-testid="buyers-love-section">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              Infrastructure Value
            </p>
            <h2 className="font-serif text-4xl font-light text-foreground mb-10">
              Why Buyers <span className="text-primary">Love This</span>
            </h2>

            <div className="flex flex-wrap justify-center gap-4 mb-10">
              {[
                "Single codebase",
                "License-based revenue",
                "Enforcement baked in",
                "Role separation clean",
                "Scales without founder",
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  className="bg-primary/10 border border-primary/30 px-6 py-3"
                >
                  <span className="text-sm text-primary font-medium">{item}</span>
                </motion.div>
              ))}
            </div>

            <p className="text-xl font-serif text-primary">
              This is sellable infrastructure, not content.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Book Diligence Section */}
      <section className="py-16 md:py-24 px-6 md:px-12 border-t border-white/5" data-testid="book-diligence-section">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              Authority Documentation
            </p>
            <h2 className="font-serif text-3xl md:text-4xl font-light text-foreground mb-6">
              Documented, Governed, <span className="text-primary">Enforced</span>
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              This platform is documented, governed, and enforced.<br />
              The book reflects the same operating logic buyers will diligence.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Developer Handoff Documentation */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#050505]" data-testid="dev-handoff-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              Prevents Rebuild Chaos
            </p>
            <h2 className="font-serif text-4xl font-light text-foreground">
              Developer <span className="text-primary">Handoff</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {/* System Overview */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-card/5 border border-white/10 p-6"
            >
              <h3 className="text-sm font-mono text-primary mb-4">SYSTEM OVERVIEW (DEV BRIEF)</h3>
              <div className="space-y-4">
                <div>
                  <p className="text-xs text-muted-foreground uppercase mb-1">Product Type:</p>
                  <p className="text-foreground">Licensing + certification platform (not LMS)</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase mb-1">Primary Value:</p>
                  <p className="text-foreground">Authority enforcement at scale</p>
                </div>
              </div>
            </motion.div>

            {/* CMS Relationships */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-card/5 border border-white/10 p-6"
            >
              <h3 className="text-sm font-mono text-primary mb-4">CMS RELATIONSHIPS</h3>
              <div className="font-mono text-sm space-y-2">
                {[
                  "Users → Operators (1:1)",
                  "Operators → Certification Modules (1:many)",
                  "Operators → Enforcement Logs (1:many)",
                  "Exams → Modules (1:1)",
                ].map((rel, i) => (
                  <div key={i} className="flex items-center gap-2 text-foreground">
                    <Link2 className="h-3 w-3 text-primary" />
                    {rel}
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Hard Rules + Test Cases */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-red-500/5 border border-red-500/20 p-6"
            >
              <h3 className="text-sm font-mono text-red-400 mb-4">HARD RULES (NON-NEGOTIABLE)</h3>
              <div className="space-y-2">
                {["No open enrollment", "No public pricing", "No gamification", "No funnels", "No testimonials"].map((rule, i) => (
                  <div key={i} className="flex items-center gap-2 text-foreground">
                    <XCircle className="h-4 w-4 text-red-400" />
                    {rule}
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-card/5 border border-white/10 p-6"
            >
              <h3 className="text-sm font-mono text-primary mb-4">TEST CASES</h3>
              <div className="space-y-2">
                {[
                  "Operator fails audit → full lockout",
                  "License expires → dashboard only",
                  "Investor logs in → no edit rights",
                  "Board logs in → metrics only",
                ].map((test, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-foreground">
                    <CheckCircle className="h-4 w-4 text-green-400" />
                    {test}
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* M&A Carve-Out Logic */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#050505]" data-testid="ma-carveout-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              Buyers Will Ask This
            </p>
            <h2 className="font-serif text-4xl font-light text-foreground">
              M&A <span className="text-primary">Carve-Out Logic</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            {/* HoldCo */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-primary/10 border border-primary/30 p-6"
            >
              <h3 className="text-sm font-mono text-primary mb-4">HOLDCO: OnPoint Authority Systems, Inc.</h3>
              <p className="text-xs text-muted-foreground uppercase mb-3">Owns:</p>
              <ul className="space-y-2">
                {["IP", "Certification", "Enforcement standards", "Brand"].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-foreground">
                    <Shield className="h-3 w-3 text-primary" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* OpCo */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-card/5 border border-white/10 p-6"
            >
              <h3 className="text-sm font-mono text-muted-foreground mb-4">OPCO(S): Licensees</h3>
              <ul className="space-y-2">
                {["Use the system", "Pay royalties", "Subject to audits"].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-foreground">
                    <div className="w-1 h-1 bg-muted-foreground rounded-full" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Sellable Units */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-card/5 border border-white/10 p-6"
            >
              <h3 className="text-sm font-mono text-primary mb-4">SELLABLE UNITS</h3>
              <p className="text-xs text-muted-foreground mb-2">You can sell:</p>
              <div className="flex flex-wrap gap-2 mb-4">
                {["License revenue stream", "Certification platform", "Enforcement framework", "Operator base", "Data room + audit history"].map((item, i) => (
                  <span key={i} className="text-xs bg-white/5 px-2 py-1 text-foreground">{item}</span>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mb-2">Or carve out:</p>
              <div className="flex flex-wrap gap-2">
                {["Education only", "Licensing only", "IP only"].map((item, i) => (
                  <span key={i} className="text-xs bg-primary/10 px-2 py-1 text-primary">{item}</span>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Buyer Optionality */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-card/5 border border-white/10 p-6"
          >
            <h3 className="text-sm font-mono text-primary mb-4">BUYER OPTIONALITY</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { type: "PE", carve: "License + enforcement" },
                { type: "Strategic", carve: "Certification IP" },
                { type: "Rollup", carve: "Operator base" },
                { type: "EdTech", carve: "Course + exams" },
              ].map((item, i) => (
                <div key={i} className="text-center p-4 bg-white/5">
                  <p className="text-sm font-medium text-foreground mb-1">{item.type}</p>
                  <p className="text-xs text-muted-foreground">{item.carve}</p>
                </div>
              ))}
            </div>
            <p className="text-sm font-serif text-primary mt-6 text-center italic">This is intentional flexibility.</p>
          </motion.div>
        </div>
      </section>

      {/* Audit Automation Flows */}
      <section className="py-16 md:py-24 px-6 md:px-12 border-t border-white/5" data-testid="audit-automation-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-red-400 mb-4">
              The Moat
            </p>
            <h2 className="font-serif text-4xl font-light text-foreground">
              Audit <span className="text-primary">Automation</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {/* Audit Triggers */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-card/5 border border-white/10 p-6"
            >
              <h3 className="text-sm font-mono text-primary mb-4">AUDIT TRIGGERS</h3>
              <div className="space-y-3">
                {[
                  { trigger: "Time-based", desc: "(quarterly)" },
                  { trigger: "Revenue-based", desc: "(threshold breach)" },
                  { trigger: "Complaint-based", desc: "" },
                  { trigger: "Randomized sampling", desc: "" },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-white/5">
                    <AlertTriangle className="h-4 w-4 text-yellow-400" />
                    <span className="text-sm text-foreground">{item.trigger}</span>
                    <span className="text-xs text-muted-foreground">{item.desc}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Automated Flow */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-card/5 border border-white/10 p-6"
            >
              <h3 className="text-sm font-mono text-primary mb-4">AUTOMATED FLOW</h3>
              <div className="space-y-2">
                {[
                  "1. Trigger fires",
                  "2. Operator notified",
                  "3. Required documents requested",
                  "4. Review window opens",
                  "5. Outcome logged: Pass / Conditional / Violation",
                ].map((step, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-foreground font-mono">
                    <ArrowRight className="h-3 w-3 text-primary" />
                    {step}
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Enforcement Logic + Dashboard */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-red-500/5 border border-red-500/20 p-6"
            >
              <h3 className="text-sm font-mono text-red-400 mb-4">ENFORCEMENT LOGIC</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-yellow-500/10 border border-yellow-500/20">
                  <span className="text-yellow-400 font-mono text-sm">Minor</span>
                  <span className="text-foreground">→ remediation task</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-orange-500/10 border border-orange-500/20">
                  <span className="text-orange-400 font-mono text-sm">Major</span>
                  <span className="text-foreground">→ probation</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-red-500/10 border border-red-500/20">
                  <span className="text-red-400 font-mono text-sm">Critical</span>
                  <span className="text-foreground">→ license revocation</span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-4">
                All actions logged. No discretion. No founder involvement.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-card/5 border border-white/10 p-6"
            >
              <h3 className="text-sm font-mono text-primary mb-4">AUDIT DASHBOARD (ADMIN)</h3>
              <div className="grid grid-cols-2 gap-3">
                {["Active operators", "Audit status", "Violations by category", "Revocations", "Revenue at risk"].map((metric, i) => (
                  <div key={i} className="bg-white/5 p-3 text-center">
                    <p className="text-xs text-muted-foreground">{metric}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Buyer Diligence Walkthrough Script */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#050505]" data-testid="diligence-walkthrough">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              Management Script
            </p>
            <h2 className="font-serif text-4xl font-light text-foreground">
              Buyer Diligence <span className="text-primary">Walkthrough</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Walkthrough Order */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-card/5 border border-white/10 p-6"
            >
              <div className="bg-primary/10 border border-primary/30 p-4 mb-6">
                <p className="text-xs text-muted-foreground uppercase mb-2">Opening:</p>
                <p className="text-foreground font-serif italic">
                  "This is a licensing and enforcement business, not an education platform."
                </p>
              </div>
              
              <h3 className="text-sm font-mono text-primary mb-4">WALKTHROUGH ORDER</h3>
              <div className="space-y-3">
                {[
                  { page: "Homepage", purpose: "positioning discipline" },
                  { page: "Systems page", purpose: "architecture clarity" },
                  { page: "Portal dashboard", purpose: "authority control" },
                  { page: "Enforcement log", purpose: "risk containment" },
                  { page: "Investor metrics", purpose: "scalability proof" },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-white/5">
                    <span className="text-xs font-mono text-primary">{String(i + 1).padStart(2, '0')}</span>
                    <span className="text-sm text-foreground">{item.page}</span>
                    <span className="text-xs text-muted-foreground">— {item.purpose}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Anchor Points + Close */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="space-y-6"
            >
              <div className="bg-card/5 border border-white/10 p-6">
                <h3 className="text-sm font-mono text-primary mb-4">BUYER ANCHOR POINTS</h3>
                <p className="text-xs text-muted-foreground mb-3">Say explicitly:</p>
                <div className="space-y-2">
                  {[
                    "Authority is revocable.",
                    "Operators are replaceable.",
                    "The system runs without founders.",
                    "Revenue is governed, not negotiated.",
                  ].map((point, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 bg-white/5">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      <span className="text-sm text-foreground">"{point}"</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-primary/10 border border-primary/30 p-6">
                <p className="text-xs text-muted-foreground uppercase mb-2">Close:</p>
                <p className="text-xl font-serif text-primary italic">
                  "You are acquiring a governed system, not a personality."
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Automation Flow Diagrams */}
      <section className="py-16 md:py-24 px-6 md:px-12 border-t border-white/5" data-testid="automation-flows">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              Text-Mapped for Dev
            </p>
            <h2 className="font-serif text-4xl font-light text-foreground">
              Automation <span className="text-primary">Flows</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Certification Flow */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-card/5 border border-white/10 p-6"
            >
              <h3 className="text-sm font-mono text-primary mb-4">CERTIFICATION FLOW</h3>
              <div className="space-y-2 font-mono text-sm">
                {["Application Approved", "Certification Access Granted", "Modules Completed", "Exam Passed", "License Eligible"].map((step, i, arr) => (
                  <div key={i}>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-primary rounded-full" />
                      <span className="text-foreground">{step}</span>
                    </div>
                    {i < arr.length - 1 && <div className="ml-1 border-l border-primary/30 h-4 my-1" />}
                  </div>
                ))}
              </div>
            </motion.div>

            {/* License Flow */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-card/5 border border-white/10 p-6"
            >
              <h3 className="text-sm font-mono text-primary mb-4">LICENSE FLOW</h3>
              <div className="space-y-2 font-mono text-sm">
                {["License Issued", "Active Monitoring", "Audit Trigger", "Pass / Conditional / Revoke"].map((step, i, arr) => (
                  <div key={i}>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-primary rounded-full" />
                      <span className="text-foreground">{step}</span>
                    </div>
                    {i < arr.length - 1 && <div className="ml-1 border-l border-primary/30 h-4 my-1" />}
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Audit Flow */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-card/5 border border-white/10 p-6"
            >
              <h3 className="text-sm font-mono text-primary mb-4">AUDIT FLOW</h3>
              <div className="space-y-2 font-mono text-sm">
                {["Trigger (Time / Event)", "Operator Notification", "Document Submission", "Review", "Outcome Logged"].map((step, i, arr) => (
                  <div key={i}>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-yellow-400 rounded-full" />
                      <span className="text-foreground">{step}</span>
                    </div>
                    {i < arr.length - 1 && <div className="ml-1 border-l border-yellow-400/30 h-4 my-1" />}
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Enforcement Flow */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-card/5 border border-red-500/20 p-6"
            >
              <h3 className="text-sm font-mono text-red-400 mb-4">ENFORCEMENT FLOW</h3>
              <div className="space-y-2 font-mono text-sm">
                {["Violation Detected", "Severity Classified", "Action Taken", "Access Adjusted", "Log Locked"].map((step, i, arr) => (
                  <div key={i}>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-red-400 rounded-full" />
                      <span className="text-foreground">{step}</span>
                    </div>
                    {i < arr.length - 1 && <div className="ml-1 border-l border-red-400/30 h-4 my-1" />}
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-center text-sm font-mono text-red-400 mt-8"
          >
            No overrides. No discretion.
          </motion.p>
        </div>
      </section>

      {/* Design System Specs */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#050505]" data-testid="design-system-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              Figma-Ready
            </p>
            <h2 className="font-serif text-4xl font-light text-foreground">
              Design <span className="text-primary">System</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Color Tokens */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-card/5 border border-white/10 p-6"
            >
              <h3 className="text-sm font-mono text-primary mb-4">COLOR TOKENS</h3>
              <div className="space-y-3">
                {[
                  { name: "Graphite", hex: "#121212", color: "bg-[#121212]" },
                  { name: "Charcoal", hex: "#1E1E1E", color: "bg-[#1E1E1E]" },
                  { name: "Soft White", hex: "#F5F5F5", color: "bg-[#F5F5F5]" },
                  { name: "Silver Accent", hex: "#BDBDBD", color: "bg-[#BDBDBD]" },
                ].map((token, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className={`w-6 h-6 ${token.color} border border-white/10`} />
                    <span className="text-sm text-foreground">{token.name}</span>
                    <span className="text-xs font-mono text-muted-foreground">{token.hex}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Typography */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-card/5 border border-white/10 p-6"
            >
              <h3 className="text-sm font-mono text-primary mb-4">TYPOGRAPHY</h3>
              <div className="space-y-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Heading</p>
                  <p className="font-serif text-xl text-foreground">Institutional Serif</p>
                  <p className="text-xs text-muted-foreground">Canela / Libre Baskerville</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Body</p>
                  <p className="text-lg text-foreground">Neutral Sans</p>
                  <p className="text-xs text-muted-foreground">Inter / Source Sans</p>
                </div>
              </div>
            </motion.div>

            {/* UI Rules */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-card/5 border border-white/10 p-6"
            >
              <h3 className="text-sm font-mono text-primary mb-4">UI RULES</h3>
              <ul className="space-y-2">
                {[
                  "No rounded corners",
                  "No icons unless functional",
                  "Links > buttons",
                  "Flat, quiet UI",
                ].map((rule, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-foreground">
                    <div className="w-1 h-1 bg-primary rounded-full" />
                    {rule}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CMS Field Schemas */}
      <section className="py-16 md:py-24 px-6 md:px-12 border-t border-white/5" data-testid="cms-schemas-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              Webflow CMS
            </p>
            <h2 className="font-serif text-4xl font-light text-foreground">
              Field <span className="text-primary">Schemas</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              {
                collection: "Authority_Content",
                fields: [
                  { name: "Title", type: "Plain Text" },
                  { name: "Slug", type: "Auto" },
                  { name: "Section", type: "Option" },
                  { name: "Visibility", type: "Option" },
                  { name: "Body", type: "Rich Text" },
                  { name: "Diagram", type: "Image" },
                ]
              },
              {
                collection: "Certification_Modules",
                fields: [
                  { name: "Module_Number", type: "Number" },
                  { name: "Title", type: "Plain Text" },
                  { name: "Required_Tier", type: "Option" },
                  { name: "Doctrine", type: "Rich Text" },
                  { name: "Exam", type: "Reference" },
                  { name: "Locked", type: "Boolean" },
                ]
              },
              {
                collection: "Operators",
                fields: [
                  { name: "Name", type: "Plain Text" },
                  { name: "User", type: "Reference" },
                  { name: "License_Status", type: "Option" },
                  { name: "Certification_%", type: "Number" },
                  { name: "Next_Audit", type: "Date" },
                  { name: "Enforcement_Flag", type: "Boolean" },
                ]
              },
              {
                collection: "Enforcement_Log",
                fields: [
                  { name: "Operator", type: "Reference" },
                  { name: "Violation_Type", type: "Option" },
                  { name: "Severity", type: "Option" },
                  { name: "Date", type: "Date" },
                  { name: "Status", type: "Option" },
                  { name: "Revocation_Trigger", type: "Boolean" },
                ]
              },
              {
                collection: "Investors",
                fields: [
                  { name: "Name", type: "Plain Text" },
                  { name: "Role", type: "Option" },
                  { name: "NDA", type: "Boolean" },
                  { name: "Metrics_Access", type: "Boolean" },
                ]
              },
              {
                collection: "Exams",
                fields: [
                  { name: "Exam_ID", type: "Plain Text" },
                  { name: "Module", type: "Reference" },
                  { name: "Pass_Score", type: "Number" },
                  { name: "Time_Limit", type: "Number" },
                  { name: "Retake_Policy", type: "Plain Text" },
                ]
              },
            ].map((schema, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="bg-card/5 border border-white/10 p-5"
              >
                <h3 className="text-sm font-mono text-primary mb-3">{schema.collection}</h3>
                <div className="space-y-1">
                  {schema.fields.map((field, i) => (
                    <div key={i} className="flex justify-between text-xs">
                      <span className="text-foreground">{field.name}</span>
                      <span className="text-muted-foreground">{field.type}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Dev Sprint Tickets */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#050505]" data-testid="sprint-tickets-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              Sprint 1 — Foundation Build
            </p>
            <h2 className="font-serif text-4xl font-light text-foreground">
              Dev <span className="text-primary">Tickets</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { id: "1", title: "Design System Setup", goal: "Lock typography, colors, spacing", deliverable: "Global styles applied site-wide" },
              { id: "2", title: "CMS Creation", goal: "Create all CMS collections", deliverable: "CMS populated with sample data" },
              { id: "3", title: "Public Pages", goal: "/, /systems, /certification, /apply", deliverable: "Pixel-accurate to Figma" },
              { id: "4", title: "Membership Roles", goal: "Implement roles + access rules", deliverable: "Role-based visibility working" },
              { id: "5", title: "Portal Dashboard", goal: "Build /portal/dashboard", deliverable: "Conditional rendering by license status" },
              { id: "6", title: "Investor Metrics", goal: "Read-only dashboard", deliverable: "Metrics visible only to Investor/Board" },
            ].map((ticket, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="bg-card/5 border border-white/10 p-5"
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs font-mono bg-primary/20 text-primary px-2 py-0.5">TICKET {ticket.id}</span>
                </div>
                <h3 className="text-sm font-medium text-foreground mb-2">{ticket.title}</h3>
                <p className="text-xs text-muted-foreground mb-2">Goal: {ticket.goal}</p>
                <p className="text-xs text-primary">Deliverable: {ticket.deliverable}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Simulated PE IC Review */}
      <section className="py-16 md:py-24 px-6 md:px-12 border-t border-white/5" data-testid="ic-review-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              What Happens in the Room
            </p>
            <h2 className="font-serif text-4xl font-light text-foreground">
              Simulated PE <span className="text-primary">IC Review</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Opening + Questions */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              <div className="bg-primary/10 border border-primary/30 p-6">
                <p className="text-xs text-muted-foreground uppercase mb-2">IC Opening Comment (Partner)</p>
                <p className="text-lg font-serif text-foreground italic">
                  "This does not look like an education business."
                </p>
                <p className="text-xs text-green-400 mt-2">✔️ Positive signal</p>
              </div>

              <div className="bg-card/5 border border-white/10 p-6">
                <h3 className="text-sm font-mono text-primary mb-4">IC QUESTIONS + OUTCOMES</h3>
                <div className="space-y-4">
                  {[
                    { q: "Is revenue defensible?", a: "License-based, revocable, enforced", reaction: "👍 Strong moat" },
                    { q: "Founder risk?", a: "Authority embedded, founder removable", reaction: "👍 Exit-ready" },
                    { q: "Scalability?", a: "License expansion, not headcount", reaction: "👍 Margin leverage" },
                    { q: "IP leakage risk?", a: "Access-controlled, audit-logged", reaction: "👍 Contained" },
                    { q: "Platform risk?", a: "Single Webflow + portable backend", reaction: "👍 Low rebuild cost" },
                  ].map((item, i) => (
                    <div key={i} className="border-l-2 border-white/10 pl-4">
                      <p className="text-sm text-foreground mb-1">❓ {item.q}</p>
                      <p className="text-xs text-muted-foreground mb-1">{item.a}</p>
                      <p className="text-xs text-green-400">{item.reaction}</p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* IC Decision */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="flex flex-col justify-center"
            >
              <div className="bg-green-500/10 border border-green-500/30 p-8">
                <p className="text-xs text-green-400 uppercase tracking-wider mb-4">IC Decision (Simulated)</p>
                <blockquote className="font-serif text-xl text-foreground leading-relaxed mb-6">
                  "This is an institutional authority platform with real enforcement logic.
                  We would underwrite this as a licensing business, not content."
                </blockquote>
                <div className="flex items-center gap-3 p-4 bg-green-500/20 border border-green-500/30">
                  <div className="w-3 h-3 bg-green-400 rounded-full" />
                  <span className="text-green-400 font-medium">Status: Advance to LOI modeling</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Buyer Takeaway */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-[#050505]" data-testid="takeaway-section">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-8">
              Buyer Takeaway
            </p>
            <blockquote className="font-serif text-2xl md:text-3xl font-light text-foreground leading-relaxed mb-10 border-l-4 border-primary pl-8 text-left">
              "This is a scalable authority platform with built-in enforcement and predictable economics."
            </blockquote>

            <Link to="/authority-review">
              <Button
                className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.15em] text-xs font-bold px-10 py-6 rounded-none gold-glow"
                data-testid="investor-cta"
              >
                Request Detailed Materials
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default InvestorAccessPage;
